import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMutation, useQuery } from "@apollo/client/react";
import { BookOpen, Gift, Home, Loader2, MailOpen, Play, Sparkles } from "lucide-react";
import { toast } from "sonner";

import { CREATE_SESSION, GENERATE_EXERCISES } from "@/graphql/mutations/sessions";
import { GET_PROGRESS_REPORT_AVAILABILITY } from "@/graphql/queries/reports";
import { AvatarStyleDialog, avatarsLabel, type useAvatarStudio } from "@/components/avatar-studio";
import { ProgressLetterMeter } from "@/components/progress-letter";
import { SubjectIcon } from "@/components/subject-icon";
import { TheoryView } from "@/components/theory-view";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { EXERCISE_COUNT_FALLBACK, exercisesLabel, rememberedExerciseCount } from "@/lib/students";
import type { GamificationResult } from "@/lib/gamification";

/**
 * The last thing a learner sees, and for a long time the least of it: a tick, a
 * score line and one grey link home. Everything they had just earned was
 * announced somewhere else — the points in a toast that had already gone, the
 * avatar as a plain outline button indistinguishable from "back", the letter not
 * at all — and there was nothing to do next but leave.
 *
 * So this screen does three jobs in order, and they are deliberately that order:
 *
 *   1. **Celebrate** what just happened, loudly and by name.
 *   2. **Hand over** what was earned — the avatar, the letter — where the win is
 *      still warm. A reward that looks like navigation is not a reward.
 *   3. **Offer the next step**, because "well done, now go away" is the one
 *      ending that makes finishing feel like stopping.
 *
 * A grown-up solving alongside the child gets the same celebration and a
 * different continuation: their next step is reading the answers, not starting
 * another session from here.
 */

type Summary = { id: string; title: string; theory: string };

type Props = {
  sessionId: string;
  studentId?: string;
  studentName?: string;
  /** A learner in their own shell. Decides where "next" leads and who owns the letter. */
  isLearner: boolean;
  subjectKey?: string | null;
  subjectName?: string | null;
  subjectIcon?: string | null;
  suggestedExerciseCount?: number | null;
  /** The most this subject may serve this learner, or null for no ceiling. */
  subjectExerciseLimit?: number | null;
  correctCount: number;
  partialCount: number;
  totalProblems: number;
  gamification: GamificationResult | null;
  /** True once completeSession has answered — before that the letter cannot know. */
  completed: boolean;
  learnedToday: Summary[];
  /** Owned by the page, so the credits query stays mounted across the submit. */
  studio: ReturnType<typeof useAvatarStudio>;
  newAvatarUrl: string | null;
};

/**
 * What the score is worth saying about it. Never a verdict on the child — the
 * bottom band still opens with the work they did, because "you got 2 of 10" is
 * something they can already see and does not need repeating as a headline.
 */
function headline(correct: number, total: number): { emoji: string; title: string; line: string } {
  const share = total > 0 ? correct / total : 0;

  if (total > 0 && correct === total) {
    return { emoji: "🏆", title: "Tobula!", line: "Visos užduotys teisingai — nė vienos pro šalį" };
  }
  if (share >= 0.8) {
    return { emoji: "🎉", title: "Šaunuolis!", line: "Beveik viskas teisingai" };
  }
  if (share >= 0.5) {
    return { emoji: "💪", title: "Puikus darbas!", line: "Daugiau nei pusė užduočių teisingai" };
  }
  return { emoji: "🌟", title: "Sesija baigta!", line: "Sunkios užduotys — bet tu jas įveikei iki galo" };
}

/** Achievements, levels and mastered skills, as things to look at rather than toasts that have gone. */
function earnedBadges(gamification: GamificationResult | null) {
  if (!gamification) return [] as { key: string; icon: string; label: string }[];

  return gamification.events.flatMap((event, index) => {
    const key = `${event.type}-${index}`;
    switch (event.type) {
      case "achievement":
        return [{ key, icon: event.achievementIcon || "🏅", label: event.achievementName || "Pasiekimas" }];
      case "level_up":
        return [{ key, icon: event.levelIcon || "⭐", label: `Naujas lygis: ${event.levelName ?? ""}`.trim() }];
      case "skill_mastered":
        return [{ key, icon: "🏅", label: `Įvaldyta: ${event.skillName ?? ""}`.trim() }];
      case "skill_unlocked":
        return [{ key, icon: "🔓", label: `Atrakinta: ${event.skillName ?? ""}`.trim() }];
      case "personal_best":
        return [{ key, icon: "🎯", label: "Naujas rekordas!" }];
      default:
        return [];
    }
  });
}

export function SessionComplete({
  sessionId,
  studentId,
  studentName,
  isLearner,
  subjectKey,
  subjectName,
  subjectIcon,
  suggestedExerciseCount,
  subjectExerciseLimit,
  correctCount,
  partialCount,
  totalProblems,
  gamification,
  completed,
  learnedToday,
  studio,
  newAvatarUrl,
}: Props) {
  const navigate = useNavigate();
  const [showAvatarStyles, setShowAvatarStyles] = useState(false);
  const [starting, setStarting] = useState(false);

  const [createSession] = useMutation(CREATE_SESSION);
  const [generateExercises] = useMutation(GENERATE_EXERCISES);

  // Asked only once the session is actually banked: eligibility counts completed
  // sessions, and this one is what may have just tipped it over.
  const { data: letterData } = useQuery(GET_PROGRESS_REPORT_AVAILABILITY, {
    variables: { studentId },
    skip: !studentId || !isLearner || !completed,
    fetchPolicy: "network-only",
  });
  const availability = (letterData as any)?.progressReportAvailability;
  const letterWaiting = availability?.allowed === true;

  const credits = studio.credits;
  const { emoji, title, line } = headline(correctCount, totalProblems);
  const badges = earnedBadges(gamification);

  // "One more like that" — the same subject, the same length they last chose,
  // held to whatever ceiling a grown-up set for this subject. The button prints
  // this number, and the server clamps the request either way — so without the
  // ceiling it would promise twenty and hand over six.
  const chosen =
    rememberedExerciseCount(studentId) ?? suggestedExerciseCount ?? EXERCISE_COUNT_FALLBACK;
  const nextCount = subjectExerciseLimit ? Math.min(chosen, subjectExerciseLimit) : chosen;

  const startAnother = async () => {
    if (!studentId || !subjectKey || starting) return;
    setStarting(true);
    try {
      const { data: created } = await createSession({
        variables: { studentId, subjectKey },
      });
      const session = (created as any)?.createSession?.session;
      if (!session) {
        toast.error((created as any)?.createSession?.errors?.[0] ?? "Nepavyko pradėti");
        return;
      }

      const { data: generated } = await generateExercises({
        variables: { sessionId: session.id, targetExerciseCount: nextCount },
      });
      const genErrors = (generated as any)?.generateExercises?.errors ?? [];
      if (genErrors.length > 0) {
        toast.error(genErrors[0]);
        return;
      }

      navigate(`/sessions/${session.id}/solve`);
    } catch {
      toast.error("Nepavyko pradėti");
    } finally {
      setStarting(false);
    }
  };

  return (
    <div className="mx-auto max-w-lg space-y-4 pb-6">
      {/* ── 1. The celebration ─────────────────────────────────────────────── */}
      <div className="relative overflow-hidden rounded-3xl border bg-gradient-to-br from-amber-400/15 via-pink-500/10 to-indigo-500/15 dark:from-amber-400/20 dark:via-pink-500/15 dark:to-indigo-500/20">
        <div className="pointer-events-none absolute -top-24 -right-20 h-64 w-64 rounded-full bg-gradient-to-br from-amber-400/30 to-pink-400/30 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-24 -left-20 h-64 w-64 rounded-full bg-gradient-to-br from-indigo-400/30 to-purple-400/30 blur-3xl" />

        <div className="relative flex flex-col items-center gap-2 px-6 py-8 text-center">
          <span className="animate-float text-6xl leading-none">{emoji}</span>
          <h1 className="bg-gradient-to-br from-foreground to-foreground/70 bg-clip-text text-3xl font-extrabold tracking-tight text-transparent sm:text-4xl">
            {title}
          </h1>
          <p className="text-sm text-muted-foreground">
            {/* A grown-up watching needs to know whose session this was; the
                child does not need to be told their own name. */}
            {isLearner ? line : (studentName ?? line)}
          </p>

          {/* The numbers, as pills rather than a sentence — same scoreboard idiom
              the letter uses, so the two read as one child's world. */}
          <div className="mt-3 flex flex-wrap items-center justify-center gap-2">
            <span className="rounded-full bg-background/70 px-3 py-1.5 text-sm font-semibold shadow-sm backdrop-blur-sm">
              ✅ {correctCount} iš {totalProblems} teisingai
            </span>
            {partialCount > 0 && (
              <span className="rounded-full bg-background/70 px-3 py-1.5 text-sm font-semibold shadow-sm backdrop-blur-sm">
                ➗ {partialCount} iš dalies
              </span>
            )}
            {gamification && (
              <span className="rounded-full bg-amber-500/15 px-3 py-1.5 text-sm font-bold text-amber-700 shadow-sm dark:text-amber-300">
                +{gamification.pointsEarned} taškų
              </span>
            )}
          </div>

          {gamification && (
            <p className="text-xs text-muted-foreground">
              Iš viso {gamification.newTotalPoints} taškų · {gamification.level.icon}{" "}
              {gamification.level.name}
            </p>
          )}

          {/* Achievements were announced in a toast that has already gone. This is
              where they stay put long enough to be read. */}
          {badges.length > 0 && (
            <div className="mt-3 flex flex-wrap items-center justify-center gap-2">
              {badges.map((badge) => (
                <span
                  key={badge.key}
                  className="inline-flex items-center gap-1.5 rounded-full border border-primary/30 bg-primary/10 px-3 py-1 text-xs font-semibold text-primary"
                >
                  <span className="text-sm leading-none">{badge.icon}</span>
                  {badge.label}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── 2. What was earned ─────────────────────────────────────────────── */}

      {/* The avatar just made. Shown before the offer, because it is the proof
          that the offer is real. */}
      {newAvatarUrl && (
        <div className="rounded-3xl border bg-card p-5 text-center shadow-sm">
          <img
            src={newAvatarUrl}
            alt="Naujas avataras"
            className="mx-auto h-32 w-32 rounded-full border-4 border-white object-cover shadow-md dark:border-white/20"
          />
          <p className="mt-3 text-sm font-semibold">Tavo naujas avataras!</p>
        </div>
      )}

      {credits > 0 && (
        <div className="animate-glow-pulse relative overflow-hidden rounded-3xl border border-amber-400/50 bg-gradient-to-br from-amber-100 via-orange-50 to-yellow-100 p-5 text-center shadow-sm dark:border-amber-400/30 dark:from-amber-500/15 dark:via-orange-500/10 dark:to-yellow-500/10">
          <span className="absolute right-4 top-4 rounded-full bg-amber-500/20 px-2.5 py-1 text-[10px] font-extrabold uppercase tracking-wider text-amber-800 dark:text-amber-200">
            Apdovanojimas
          </span>

          <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 text-white shadow-lg">
            <Gift className="h-7 w-7" />
          </div>

          <p className="text-lg font-bold">
            {newAvatarUrl ? "Gali sukurti dar vieną!" : "Uždirbai naują avatarą!"}
          </p>
          <p className="mt-1 text-sm text-muted-foreground">
            {newAvatarUrl
              ? `Tau liko ${avatarsLabel(credits)}`
              : credits === 1
                ? "Už atsakytas užduotis — pasirink stilių ir jis tavo"
                : `Turi ${avatarsLabel(credits)} — pasirink stilių`}
          </p>

          <Button
            size="lg"
            onClick={() => setShowAvatarStyles(true)}
            disabled={studio.isGenerating}
            className="mt-4 w-full rounded-2xl bg-gradient-to-r from-amber-500 to-orange-500 text-base font-bold text-white shadow-lg transition-transform hover:from-amber-600 hover:to-orange-600 hover:shadow-xl active:scale-[0.98]"
          >
            {studio.isGenerating ? (
              <>
                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                Kuriu…
              </>
            ) : (
              <>
                <Sparkles className="mr-2 h-5 w-5" />
                {newAvatarUrl ? "Sukurti dar vieną" : "Atsiimti avatarą"}
              </>
            )}
          </Button>
        </div>
      )}

      <AvatarStyleDialog
        open={showAvatarStyles}
        onOpenChange={setShowAvatarStyles}
        credits={credits}
        isGenerating={studio.isGenerating}
        onPick={studio.generate}
      />

      {/* The letter, in the words it is called everywhere on the child's side. */}
      {letterWaiting && (
        <div className="relative overflow-hidden rounded-3xl border border-emerald-500/40 p-5 text-center shadow-sm">
          <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/15 via-amber-500/10 to-pink-500/15" />
          <div className="relative space-y-1">
            <p className="text-4xl">💌</p>
            <p className="text-lg font-bold">Hurá! Tau atėjo naujas laiškas</p>
            <p className="text-sm text-muted-foreground">
              Atidaryk ir pažiūrėk, kuo gali didžiuotis
            </p>
            <Button
              size="lg"
              asChild
              className="mt-3 w-full rounded-2xl bg-gradient-to-r from-emerald-500 to-teal-500 text-base font-bold text-white shadow-lg hover:from-emerald-600 hover:to-teal-600"
            >
              <Link to="/profile/reports">
                <MailOpen className="mr-2 h-5 w-5" />
                Atidaryti laišką
              </Link>
            </Button>
          </div>
        </div>
      )}

      {/* ── 3. What next ───────────────────────────────────────────────────── */}
      <Card className="rounded-3xl">
        <CardContent className="space-y-3 py-5">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            Kas toliau?
          </h2>

          {isLearner ? (
            <>
              {subjectKey && (
                <Button
                  size="lg"
                  onClick={startAnother}
                  disabled={starting}
                  className="h-auto w-full justify-start gap-3 rounded-2xl py-4 text-left"
                >
                  {starting ? (
                    <Loader2 className="h-6 w-6 shrink-0 animate-spin" />
                  ) : subjectIcon ? (
                    <SubjectIcon icon={subjectIcon} className="h-6 w-6 shrink-0" />
                  ) : (
                    <Play className="h-6 w-6 shrink-0" />
                  )}
                  <span className="min-w-0 flex-1">
                    <span className="block font-bold">
                      {starting ? "Ruošiu…" : `Dar viena sesija${subjectName ? ` — ${subjectName}` : ""}`}
                    </span>
                    <span className="block text-xs font-normal opacity-80">
                      {exercisesLabel(nextCount)}
                    </span>
                  </span>
                </Button>
              )}

              <Button variant="outline" size="lg" asChild className="w-full rounded-2xl">
                <Link to="/">
                  <Home className="mr-2 h-4 w-4" />
                  Rinktis kitą dalyką
                </Link>
              </Button>

              {/* How close the next letter is. One line, and only when there is
                  not already one waiting above. */}
              {availability && !letterWaiting && <ProgressLetterMeter availability={availability} />}
            </>
          ) : (
            <>
              <Button size="lg" asChild className="w-full rounded-2xl">
                <Link to={`/sessions/${sessionId}`}>Peržiūrėti atsakymus</Link>
              </Button>
              <Button variant="outline" size="lg" asChild className="w-full rounded-2xl">
                <Link to="/sessions/new">
                  <Play className="mr-2 h-4 w-4" />
                  Nauja sesija
                </Link>
              </Button>
            </>
          )}
        </CardContent>
      </Card>

      {/* The rules behind today's work, once it is done — see "How Theory Is
          Presented". They cost no attention that was wanted elsewhere here. */}
      {learnedToday.length > 0 && (
        <Card className="rounded-3xl">
          <CardContent className="py-5">
            <h3 className="mb-4 flex items-center gap-2 text-lg font-semibold">
              <BookOpen className="h-5 w-5 text-cyan-600 dark:text-cyan-400" />
              Ką šiandien išsiaiškinai
            </h3>
            <div className="space-y-5">
              {learnedToday.map((summary) => (
                <div key={summary.id}>
                  <p className="mb-1 text-sm font-medium text-muted-foreground">{summary.title}</p>
                  <TheoryView html={summary.theory} className="text-sm" keyPointsOnly />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
